import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/common/StatusBadge";
import { statusTone } from "@/lib/format";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

// value: number, max: for the progress ring scale, status: string
export const IndexScorecard = ({
    label,
    value,
    display,
    status,
    max = 1,
    info,
    index = 0,
}) => {
    const tone = statusTone(status);
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const toneStroke = {
        success: "hsl(var(--success))",
        warning: "hsl(var(--warning))",
        destructive: "hsl(var(--destructive))",
        accent: "hsl(var(--accent))",
        muted: "hsl(var(--muted-foreground))",
    }[tone];

    const r = 26;
    const c = 2 * Math.PI * r;

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="h-full"
        >
            <Card className="flex h-full flex-col border-border/70 bg-card p-5 shadow-card">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-snug text-foreground">{label}</p>
                    {info && (
                        <Tooltip>
                            <TooltipTrigger className="shrink-0 text-muted-foreground/70 transition-colors hover:text-foreground">
                                <Info className="h-4 w-4" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[240px] text-xs leading-relaxed">
                                {info}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>

                <div className="mt-4 flex items-center gap-4">
                    <div className="relative h-[68px] w-[68px] shrink-0">
                        <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90">
                            <circle
                                cx="32" cy="32" r={r} fill="none"
                                stroke="hsl(var(--muted))" strokeWidth="6"
                            />
                            <motion.circle
                                cx="32" cy="32" r={r} fill="none"
                                stroke={toneStroke} strokeWidth="6" strokeLinecap="round"
                                strokeDasharray={c}
                                initial={{ strokeDashoffset: c }}
                                animate={{ strokeDashoffset: c - (pct / 100) * c }}
                                transition={{ duration: 0.9, delay: 0.15 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                            />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className={cn("text-lg font-semibold tabular", `text-${tone === "muted" ? "foreground" : tone}`)}>
                                {display}
                            </span>
                        </div>
                    </div>
                    <div className="min-w-0 mt-auto">
                        <StatusBadge status={status} />
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};
